import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { EASE } from "./motion";

const PROJECT_TYPES = [
  { key: "new", label: "New site" },
  { key: "redesign", label: "Redesign" },
  { key: "maintenance", label: "Ongoing maintenance" },
];

const TIMELINES = [
  { key: "asap", label: "ASAP" },
  { key: "soon", label: "1–2 months" },
  { key: "exploring", label: "Just exploring" },
];

// Real branching logic, not a per-combination lookup table.
export function getRecommendation(projectType, timeline) {
  if (projectType === "maintenance") {
    return {
      tier: "Maintenance",
      price: "$125–200/mo",
      blurb: "Ongoing hosting, security, and small content changes — no new build needed.",
    };
  }
  if (timeline === "exploring") {
    return {
      tier: "Starter",
      price: "$1,800",
      blurb: "A focused 5-page build to get you live without overcommitting while you're still deciding.",
    };
  }
  if (projectType === "new" && timeline === "asap") {
    return {
      tier: "Custom",
      price: "$5,000+",
      blurb: "A new site on a tight timeline usually means ecommerce, integrations, or scope that needs a real build-out.",
    };
  }
  return {
    tier: "Growth",
    price: "$3,400+",
    blurb: "Booking automation, analytics, and Google Business setup — the tier most builds actually need.",
  };
}

const STEPS = ["Project type", "Timeline", "Recommendation"];

function OptionCard({ label, active, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`w-full rounded-lg border px-5 py-4 text-left font-serif text-xl transition-colors duration-200 ${
        active
          ? "border-pine bg-pine text-paper"
          : "border-hairline bg-panel/60 text-ink hover:border-pine/50"
      }`}
    >
      {label}
    </button>
  );
}

export default function ReconQuiz({ onComplete }) {
  const [step, setStep] = useState(0);
  const [projectType, setProjectType] = useState(null);
  const [timeline, setTimeline] = useState(null);

  const recommendation =
    projectType && timeline ? getRecommendation(projectType, timeline) : null;

  const selectProjectType = (key) => {
    setProjectType(key);
    setStep(1);
  };
  const selectTimeline = (key) => {
    setTimeline(key);
    setStep(2);
  };
  const goBack = () => setStep((s) => Math.max(0, s - 1));

  return (
    <div data-testid="recon-quiz">
      <div className="flex items-center justify-between">
        {step > 0 ? (
          <button
            onClick={goBack}
            data-testid="recon-quiz-back"
            className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-ink-dim transition-colors hover:text-ink"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </button>
        ) : (
          <span />
        )}
        <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-dim">
          Step {step + 1} / {STEPS.length} — {STEPS[step]}
        </span>
      </div>

      <div className="relative mt-6 min-h-[280px]">
        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div
              key="step-0"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.35, ease: EASE }}
            >
              <h3 className="font-serif text-2xl text-ink">What are we building?</h3>
              <div className="mt-5 space-y-3">
                {PROJECT_TYPES.map((t) => (
                  <OptionCard
                    key={t.key}
                    label={t.label}
                    active={projectType === t.key}
                    onClick={() => selectProjectType(t.key)}
                    testId={`recon-quiz-type-${t.key}`}
                  />
                ))}
              </div>
            </motion.div>
          )}

          {step === 1 && (
            <motion.div
              key="step-1"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.35, ease: EASE }}
            >
              <h3 className="font-serif text-2xl text-ink">What's the timeline?</h3>
              <div className="mt-5 space-y-3">
                {TIMELINES.map((t) => (
                  <OptionCard
                    key={t.key}
                    label={t.label}
                    active={timeline === t.key}
                    onClick={() => selectTimeline(t.key)}
                    testId={`recon-quiz-timeline-${t.key}`}
                  />
                ))}
              </div>
            </motion.div>
          )}

          {step === 2 && recommendation && (
            <motion.div
              key="step-2"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.35, ease: EASE }}
              data-testid="recon-quiz-recommendation"
            >
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-rust">
                Recommended
              </p>
              <h3 className="mt-2 font-serif text-4xl text-ink">
                {recommendation.tier}
              </h3>
              <p className="mt-1 font-mono text-2xl text-pine">{recommendation.price}</p>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-ink-dim">
                {recommendation.blurb}
              </p>
              <button
                onClick={() =>
                  onComplete({ projectType, timeline, ...recommendation })
                }
                data-testid="recon-quiz-book-call"
                className="mt-7 rounded-full bg-pine px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.16em] text-paper transition-transform duration-300 hover:scale-[1.03] active:scale-95"
              >
                Book your recon call
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
